import React, { useEffect, useMemo, useRef } from 'react';

import AddIconButton from '../components/AddIconButton';
import DeleteIconButton from '../components/DeleteIconButton';

const YearsEditor = ({ years, setYears, onResolvedChange }) => {
    /* ---------- propagate resolved years upward ---------- */

    const lastResolvedRef = useRef(null);

    useEffect(() => {
        const initialResolved = years
            .filter(y => Number.isInteger(y.year))
            .map(y => ({
                year: y.year,
                months: y.months
                    .map((enabled, i) => (enabled ? i + 1 : null))
                    .filter(Boolean)
            }));


        lastResolvedRef.current = JSON.stringify(initialResolved);
        // intentionally no onResolvedChange here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const resolved = years
                .filter(y => Number.isInteger(y.year))
                .map(y => ({
                    year: y.year,
                    months: y.months
                        .map((enabled, i) => (enabled ? i + 1 : null))
                        .filter(Boolean)
                }));

            const prev = lastResolvedRef.current;
            const next = JSON.stringify(resolved);

            if (prev !== next) {
                lastResolvedRef.current = next;
                onResolvedChange(resolved);
            }
        }, 400);

        return () => clearTimeout(timeoutId);
        // intentionally no onResolvedChange here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [years]);

    /* ---------- helpers ---------- */

    const parseYearInput = (value) => {
        const parsed = parseInt(value, 10);
        if (Number.isNaN(parsed)) {
            return null;
        }
        return parsed;
    };

    const nextYearValue = useMemo(() => {
        const last = years.at(-1);
        if (!last) {
            return new Date().getFullYear();
        }

        // Prefer validated numeric year, fallback to parsing yearInput, fallback to current year.
        const base =
            Number.isInteger(last.year)
                ? last.year
                : parseYearInput(last.yearInput);

        const baseYear = Number.isInteger(base) ? base : new Date().getFullYear();

        // Force numeric addition (prevents "2025" + 1 => "20251")
        return Number(baseYear) + 1;
    }, [years]);

    /* ---------- handlers ---------- */

    const updateYearInput = (index, value) => {
        setYears(prev => {
            const copy = [...prev];
            const y = { ...copy[index] };

            y.yearInput = value;
            y.dirty = true;

            const parsed = parseYearInput(value);

            // Only commit to model year when it looks like a proper 4-digit year
            if (parsed !== null && value.length === 4) {
                y.year = parsed;
                y.dirty = false;
            }

            copy[index] = y;
            return copy;
        });
    };

    const toggleMonth = (yearIndex, monthIndex) => {
        setYears(prev => {
            const copy = [...prev];
            const y = { ...copy[yearIndex] };

            y.months = y.months.map((m, i) => (i === monthIndex ? !m : m));

            copy[yearIndex] = y;
            return copy;
        });
    };

    const removeYear = index => {
        setYears(prev => prev.filter((_, i) => i !== index));
    };

    const addYear = () => {
        setYears(prev => ([
            ...prev,
            {
                yearInput: nextYearValue,
                year: nextYearValue,
                months: Array(12).fill(true),
                dirty: false
            }
        ]));
    };

    /* ---------- render ---------- */

    return (
        <>
            <div className="overflow-y-auto max-h-56 rounded bg-gray-50 border border-gray-300 shadow-inner shadow-sm p-2">
                {years.map((year, yi) => (
                    <div key={yi} className="flex flex-row items-middle">

                        <input
                            type="text"
                            style={{ width: '70px' }}
                            className={`border my-1 py-2 px-3 text-grey-darkest ${year.dirty ? 'border-red-500' : ''}`}
                            value={year.yearInput}
                            onChange={e => updateYearInput(yi, e.target.value)}
                        />

                        <div className="flex flex-row p-2">
                            {year.months.map((enabled, month) => (
                                <div
                                    key={month}
                                    onClick={() => toggleMonth(yi, month)}
                                    className="mx-1 cursor-pointer"
                                >
                                    {enabled && (
                                        <div className="inline-flex items-center my-1 px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            {new Date(Number.isInteger(year.year) ? year.year : 2000, month).toLocaleString('default', { month: 'short' })}
                                        </div>
                                    )}
                                    {!enabled && (
                                        <div className="inline-flex items-center my-1 px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            {new Date(Number.isInteger(year.year) ? year.year : 2000, month).toLocaleString('default', { month: 'short' })}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="w-1/3 my-1 p-2 flex justify-items-stretch">
                            {years.length > 1 && (
                                <div className="justify-self-end">
                                    <DeleteIconButton onClick={() => removeYear(yi)} title="Delete year" />
                                </div>
                            )}
                            {years.length === 1 && <div style={{ width: '30px' }}></div>}
                        </div>

                    </div>
                ))}

                <div className="w-1/3 flex justify-items-stretch my-4 mx-1">
                <div className="justify-self-end">
                    <AddIconButton onClick={addYear} />
                </div>
            </div>
            </div>

            
        </>
    );
};

export default YearsEditor;